// NPM Imports
import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import amplitude from "amplitude-js";
import { Toastr, Button, Label, Input } from "@revvsales/components";

// Components Imports
import { ReactSelect } from "../../commonComponents";
import ProductConfiguration from "./ProductConfiguration";

// Actions Imports
import ProductActions from "../../actions/ProductActions";
import { GlobalUtils } from "../../utils";
import { GRID, EVENTS } from "../../constants";
// Internal Imports

// Destructuring
const { EVENT_NAMES } = EVENTS;
const { PRODUCTS_COLUMNS } = GRID;
const { handleApiErrMsg } = GlobalUtils;
const { updateProductData, togglePopUp } = ProductActions;

class AddProduct extends Component {
  state = {
    active: true,
    category: [],
    code: "",
    codeToSku: false,
    productConfig: {},
    description: "",
    family: {},
    id: null,
    isEdit: false,
    productName: "",
    sku: "",
    type: {},
    isSaving: false,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.data !== null && !prevState.isEdit) {
      const { fieldMapIndex, data } = nextProps;
      let familyData = {};
      // Get family data from Id
      const selectedFamily = nextProps.productFamilies.find(
        item => item.id === data[fieldMapIndex.family],
      );
      if (selectedFamily) {
        familyData = {
          id: selectedFamily.id,
          value: selectedFamily.name,
          label: selectedFamily.name,
        };
      }
      return {
        active: data[fieldMapIndex.active],
        category: data[fieldMapIndex.category],
        code: data[fieldMapIndex.code],
        productConfig: data[fieldMapIndex.config_type],
        description: data[fieldMapIndex.desc],
        family: familyData,
        id: data.id,
        isEdit: true,
        productName: data[fieldMapIndex.name],
        sku: data[fieldMapIndex.sku],
        type: data[fieldMapIndex.ptype],
        codeToSku: data[fieldMapIndex.sku] === data[fieldMapIndex.code],
      };
    }
    return null;
  }

  _onInputChange = (event, field) => {
    const obj = {};
    if (this.state.codeToSku && field === "code") {
      obj.sku = event.target.value;
    }
    obj[field] = event.target.value;
    this.setState(obj);
  };

  _onCheckChange = () => {
    this.setState(prevState => ({ active: !prevState.active }));
  };

  _onSelectChange = (field, value) => {
    const obj = {};
    obj[field] = value;
    this.setState(obj);
  };

  _copyCodeToSku = () => {
    this.setState(
      prevState => ({ codeToSku: !prevState.codeToSku }),
      () => {
        if (this.state.codeToSku) {
          this.setState(prevState => ({ sku: prevState.code }));
        }
      },
    );
  };

  _isFormValid = () => {
    const { code, productConfig, productName, sku, type } = this.state;
    return (
      code === "" ||
      Object.keys(productConfig).length === 0 ||
      productName === "" ||
      sku === "" ||
      Object.keys(type).length === 0
    );
  };

  _onSubmit = e => {
    e.preventDefault();
    const {
      active,
      category,
      code,
      description,
      family,
      id,
      isEdit,
      productConfig,
      productName,
      sku,
      type,
    } = this.state;
    if (
      sku !== "" &&
      code !== "" &&
      productName !== "" &&
      Object.keys(type).length > 0 &&
      Object.keys(productConfig).length > 0
    ) {
      this.setState({ isSaving: true });
      const { fieldMapIndex } = this.props;
      const obj = {
        [PRODUCTS_COLUMNS.ID]: id,
        [fieldMapIndex[PRODUCTS_COLUMNS.ACTIVE]]: active,
        [fieldMapIndex[PRODUCTS_COLUMNS.CATEGORY]]: category,
        [fieldMapIndex[PRODUCTS_COLUMNS.CODE]]: code,
        [fieldMapIndex[PRODUCTS_COLUMNS.CONFIG_TYPE]]:
          productConfig.value || productConfig,
        [fieldMapIndex[PRODUCTS_COLUMNS.DESC]]: description,
        [fieldMapIndex[PRODUCTS_COLUMNS.FAMILY]]: family.id,
        [fieldMapIndex[PRODUCTS_COLUMNS.FAMILY_NAME]]: family.value,
        [fieldMapIndex[PRODUCTS_COLUMNS.NAME]]: productName,
        [fieldMapIndex[PRODUCTS_COLUMNS.SKU]]: sku,
        [fieldMapIndex[PRODUCTS_COLUMNS.TYPE]]: type.value || type,
      };
      this.props
        .updateProductData(obj, isEdit)
        .then(() => {
          // amplitude call
          if (!isEdit) {
            const eventProperties = {
              productConfiguration: productConfig.value,
              productType: type.value,
            };
            if (category && category.length > 0) {
              eventProperties.productCategory = category.map(i => i.value);
            }
            amplitude
              .getInstance()
              .logEvent(EVENT_NAMES.PRODUCT_CREATED, eventProperties);
          }
          // amplitude call
          this.setState({ isSaving: false });
          this.props.nukePopupData();
          this.props.togglePopUp(false);
          Toastr.showToastr({
            type: "success",
            message: isEdit
              ? "Product has been updated successfully."
              : "Product has been added successfully.",
            timeout: 2000,
          });
        })
        .catch(err => {
          this.setState({ isSaving: false });
          this.props.nukePopupData();
          this.props.togglePopUp(false);

          Toastr.showToastr({
            type: "failure",
            message: handleApiErrMsg(err),
            timeout: 3000,
          });
        });
    } else {
      Toastr.showToastr({
        type: "warning",
        message: "Please fill in the required fields.",
        timeout: 2000,
      });
    }
  };

  render() {
    const {
      active,
      category,
      code,
      codeToSku,
      description,
      family,
      isEdit,
      productConfig,
      productName,
      sku,
      type,
    } = this.state;
    const { categories, configType, productFamilies } = this.props;
    return (
      <div className="addproduct-wrapper" data-testid="addproduct-popup">
        <div className="addproduct-title">
          {isEdit ? "Update Product" : "Add new product"}
          <span className="pricingmethodwrapper-subheader">
            Configure your product
          </span>
        </div>
        <form onSubmit={this._onSubmit} className="addproduct-form">
          <div className="addproduct-div-wrapper">
            <span className="addproduct-label">
              Product Name <span className="required-input">*</span>
            </span>
            <Input
              autoFocus
              data-testid="addproduct-product-name-field"
              placeholder="Enter Product Name"
              value={productName}
              onChange={e => this._onInputChange(e, "productName")}
              data-test="addproduct-productname-input"
            />
          </div>

          <div className="addproduct-div-wrapper">
            <Label htmlFor="addproduct-checkbox">
              <Input
                id="addproduct-checkbox"
                data-testid="addproduct-checkbox-active"
                type="checkbox"
                value=""
                checked={active}
                name="ACTIVE"
                onChange={this._onCheckChange}
                data-test="addproduct-active-checkbox"
              />
              <span className="addproduct-checkbox-value">Active</span>
            </Label>
          </div>

          <div className="addproduct-div-wrapper">
            <Label htmlFor="addproduct-checkbox-codeToSku">
              <Input
                id="addproduct-checkbox-codeToSku"
                data-testid="addproduct-checkbox-codeToSku"
                type="checkbox"
                value=""
                checked={codeToSku}
                name="ACTIVE"
                onChange={this._copyCodeToSku}
                data-test="addproduct-copycodetosku-checkbox"
              />
              <span className="addproduct-checkbox-value">
                SKU SAME AS CODE
              </span>
            </Label>
          </div>

          <div className="addproduct-div-wrapper">
            <span className="addproduct-label">
              Code <span className="required-input">*</span>
            </span>
            <Input
              data-testid="addproduct-code-field"
              placeholder="Enter Code"
              value={code}
              onChange={e => this._onInputChange(e, "code")}
              data-test="addproduct-code-input"
            />
          </div>

          <div className="addproduct-div-wrapper">
            <span className="addproduct-label">
              SKU <span className="required-input">*</span>
            </span>
            <Input
              data-testid="addproduct-sku-field"
              placeholder="Enter SKU"
              value={sku}
              onChange={e => this._onInputChange(e, "sku")}
              disabled={codeToSku}
              data-test="addproduct-sku-input"
            />
          </div>
          <ProductConfiguration
            title="Product Configuration"
            optionData={configType}
            productValue={productConfig}
            onSelectChange={this._onSelectChange}
          />
          <div className="addproduct-div-wrapper">
            <span className="addproduct-label">
              Product Type <span className="required-input">*</span>
            </span>
            <ReactSelect
              isSearchable
              valueKey="name"
              onChange={value => this._onSelectChange("type", value)}
              options={this.props.productType}
              value={type}
              data-test="addproduct-producttype-select"
            />
          </div>
          <div className="addproduct-div-wrapper">
            <span className="addproduct-label">Family</span>
            <ReactSelect
              isSearchable
              valueKey="name"
              onChange={value => this._onSelectChange("family", value)}
              options={productFamilies}
              value={family}
              data-test="addproduct-family-select"
            />
          </div>
          <div className="addproduct-div-wrapper">
            <span className="addproduct-label">Category</span>
            <ReactSelect
              isMulti
              isSearchable
              onChange={value => this._onSelectChange("category", value)}
              options={categories}
              value={category}
              valueKey="name"
              data-test="addproduct-category-select"
            />
          </div>
          <div className="addproduct-div-wrapper">
            <span className="addproduct-label">Description</span>
            <Input
              placeholder="Enter Description"
              value={description}
              onChange={e => this._onInputChange(e, "description")}
              data-test="addproduct-description-input"
            />
          </div>
          <div className="addproduct-info">
            <i className="addproduct-info-icon">
              <FontAwesomeIcon icon={["far", "info-circle"]} />
            </i>
            You need to configure your product details here, then add the
            product to the required price books. It helps you define your
            product pricing per market segment, charge type, pricing method,
            etc.
          </div>
          <div className="addproduct-footer">
            <div className="addproduct-footer-actions">
              <Button
                className="btn-md"
                data-testid="addproduct-popup-submit-btn"
                disabled={this._isFormValid()}
                isLoading={this.state.isSaving}
                onClick={this._onSubmit}
                type="submit"
                data-test="addproduct-update-btn">
                {isEdit ? "UPDATE" : "ADD"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

AddProduct.defaultProps = {
  fieldMapIndex: null,
};

AddProduct.propTypes = {
  // Props
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fieldMapIndex: PropTypes.shape({}),

  // Actions
  updateProductData: PropTypes.func.isRequired,
  togglePopUp: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const { categories, productFamilies, metaData, props } = state.ProductsStore;
  return {
    categories,
    productFamilies,
    fieldMapIndex: metaData.fieldMapIndex,
    productType: props && props.ptype,
    configType: props && props.config_type,
  };
};

const mapDispatchToProps = {
  updateProductData,
  togglePopUp,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AddProduct);



// WEBPACK FOOTER //
// ./src/components/Products/AddProduct.js